import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/redefinir-senha")({
  component: RedefinirSenhaPage,
});

function RedefinirSenhaPage() {
  const navigate = useNavigate();
  const [temSessao, setTemSessao] = useState(false);
  const [senha, setSenha] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [concluido, setConcluido] = useState(false);

  useEffect(() => {
    // O link do e-mail abre uma sessão temporária de recuperação.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) setTemSessao(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setTemSessao(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (senha.length < 8) {
      setError("A senha deve ter pelo menos 8 caracteres");
      return;
    }
    if (senha !== confirmar) {
      setError("As senhas não coincidem");
      return;
    }
    setIsLoading(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password: senha });
      if (updErr) {
        setError("Não foi possível redefinir a senha. Solicite um novo link.");
        return;
      }
      setConcluido(true);
      toast.success("Senha redefinida com sucesso!");
      setTimeout(() => navigate({ to: "/login", replace: true }), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-foreground tracking-tight">
          Studio <span className="text-primary">Tattoo</span>
        </h1>
      </div>

      <Card className="w-full max-w-md bg-card border-border shadow-sm">
        <CardHeader className="pt-8 pb-4 text-center">
          <h2 className="text-xl font-bold text-foreground">Redefinir senha</h2>
        </CardHeader>
        <CardContent className="space-y-6">
          {concluido ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="text-emerald-600" size={40} />
              <p className="text-sm text-muted-foreground">
                Senha alterada. Redirecionando para o login...
              </p>
            </div>
          ) : !temSessao ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <AlertCircle className="text-primary" size={40} />
              <p className="text-sm text-muted-foreground">
                Link inválido ou expirado. Volte ao login e solicite um novo link em
                “Esqueci minha senha”.
              </p>
              <Link
                to="/login"
                className="text-sm text-primary font-semibold underline decoration-border underline-offset-4"
              >
                Voltar ao login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                  Nova senha
                </Label>
                <div className="relative">
                  <Input
                    type={showSenha ? "text" : "password"}
                    placeholder="••••••••"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    required
                    className="bg-background border-border text-foreground focus:border-primary h-12 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenha(!showSenha)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                  Confirmar nova senha
                </Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmar}
                  onChange={(e) => setConfirmar(e.target.value)}
                  required
                  className="bg-background border-border text-foreground focus:border-primary h-12"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-primary text-sm bg-primary/10 p-3 rounded-lg border border-primary/20">
                  <AlertCircle size={16} />
                  <p>{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 disabled:opacity-50"
              >
                {isLoading ? "Salvando..." : "Salvar nova senha"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
